import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Sun, Factory, Snowflake, Package, ForkKnife, Fire, Truck,
  ClipboardText, Plus, X, ArrowClockwise, Warning, CheckCircle, Clock
} from "@phosphor-icons/react";

const DEPARTMENTS = [
  { key: "preparation", label: "Preparation", icon: ForkKnife, tone: "bg-sky-100 text-sky-700" },
  { key: "cooking",     label: "Cooking",     icon: Fire,      tone: "bg-orange-100 text-orange-700" },
  { key: "loading",     label: "Loading",     icon: Snowflake, tone: "bg-cyan-100 text-cyan-700" },
  { key: "packing",     label: "Packing",     icon: Package,   tone: "bg-emerald-100 text-emerald-700" },
  { key: "dispatch",    label: "Dispatch",    icon: Truck,     tone: "bg-violet-100 text-violet-700" },
];

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const IST = "Asia/Kolkata";
    return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: IST });
  } catch { return iso; }
}

function AssignmentCard({ dept, names, onSave }) {
  const [items, setItems] = useState(names || []);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setItems(names || []); }, [names]);

  const persist = async (next) => {
    setBusy(true);
    try { await onSave(dept.key, next); }
    finally { setBusy(false); }
  };
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    const next = [...items, v];
    setItems(next); setDraft(""); persist(next);
  };
  const remove = (i) => {
    const next = items.filter((_, k) => k !== i);
    setItems(next); persist(next);
  };
  const Icon = dept.icon;
  return (
    <Card className="shadow-none" data-testid={`assign-${dept.key}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 rounded-md flex items-center justify-center ${dept.tone}`}>
            <Icon size={16} weight="duotone"/>
          </div>
          <div>
            <div className="font-semibold text-sm">{dept.label}</div>
            <div className="overline">{items.length} assigned</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-2 min-h-[24px]">
          {items.map((n, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium"
                  data-testid={`assign-${dept.key}-name-${i}`}>
              {n}
              <button onClick={() => remove(i)} className="text-muted-foreground hover:text-red-600"
                      aria-label="remove" disabled={busy}>
                <X size={11}/>
              </button>
            </span>
          ))}
          {items.length === 0 && (
            <span className="text-[11px] text-muted-foreground italic">nobody yet</span>
          )}
        </div>
        <div className="flex gap-1.5">
          <Input value={draft} onChange={e => setDraft(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && add()}
                 placeholder="Add name"
                 className="h-8 text-xs"
                 data-testid={`assign-${dept.key}-input`}/>
          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={add} disabled={busy || !draft.trim()}
                  data-testid={`assign-${dept.key}-add`}>
            <Plus size={13}/>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function FactoryToday() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/factory-today");
      setData(data);
    } catch (e) { toast.error("Failed to load Factory Today"); }
    finally { setRefreshing(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveDept = useCallback(async (dept, names) => {
    try {
      const { data: next } = await api.put("/factory-today/assignments", { [dept]: names });
      setData(prev => prev ? { ...prev, assignments: next } : prev);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  }, []);

  if (!data) {
    return (
      <Card className="shadow-none mb-6">
        <CardContent className="p-6 text-muted-foreground text-sm">Loading Factory Today…</CardContent>
      </Card>
    );
  }

  const t = data.totals;
  const nowLabel = new Date(data.date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });

  return (
    <section className="mb-8" data-testid="factory-today">
      {/* Section header */}
      <div className="flex items-start justify-between mb-3 gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider mb-1">
            <Sun size={12} weight="fill"/>Factory Today
          </div>
          <h2 className="font-display font-black text-2xl tracking-tight">{nowLabel}</h2>
          <div className="text-xs text-muted-foreground mt-0.5">
            {data.batches_today} batches created today · Auto-refreshed at {fmtTime(data.generated_at)} IST
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="factory-today-refresh">
          <ArrowClockwise size={14} className={`mr-1 ${refreshing ? "animate-spin" : ""}`}/>
          Refresh
        </Button>
      </div>

      {/* Row 1: Production Plan + Totals */}
      <Card className="shadow-none mb-4" data-testid="factory-today-plan">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
              <Factory size={18}/>Today's Production
            </div>
            <div className="hidden sm:flex items-center gap-4 mono">
              <span className="text-xs text-muted-foreground">Wet <span className="text-foreground font-bold ml-1">{t.planned_kg} kg</span></span>
              <span className="text-xs text-muted-foreground">Dry <span className="text-emerald-600 font-bold ml-1">{t.expected_dry_kg} kg</span></span>
              <span className="text-xs text-muted-foreground">Pouches <span className="text-primary font-bold ml-1">{t.expected_pouches}</span></span>
            </div>
          </div>

          {data.production_plan.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4">
              No batches created today yet. Open the <span className="mono font-semibold">Smart Planner</span> and approve today's plan.
            </div>
          ) : (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-border">
                    <th className="py-2 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Dish</th>
                    <th className="text-right py-2 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Batches</th>
                    <th className="text-right py-2 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Wet kg</th>
                    <th className="text-right py-2 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Dry kg</th>
                    <th className="text-right py-2 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Pouches</th>
                  </tr>
                </thead>
                <tbody>
                  {data.production_plan.map(p => (
                    <tr key={p.sku_id} className="border-b border-border/40 last:border-0" data-testid={`plan-${p.sku_code}`}>
                      <td className="py-2.5">
                        <div className="font-semibold">{p.sku_name}</div>
                        <div className="mono text-[11px] text-muted-foreground">{p.sku_code} · {p.category}</div>
                      </td>
                      <td className="text-right mono">{p.batches}</td>
                      <td className="text-right mono font-bold">{p.planned_kg}</td>
                      <td className="text-right mono font-bold text-emerald-600">{p.expected_dry_kg}</td>
                      <td className="text-right mono font-bold text-primary">{p.expected_pouches}</td>
                    </tr>
                  ))}
                  <tr className="bg-muted/30">
                    <td className="py-2.5 font-bold text-sm">TOTAL</td>
                    <td className="text-right mono font-bold">{t.batches}</td>
                    <td className="text-right mono font-bold">{t.planned_kg}</td>
                    <td className="text-right mono font-bold text-emerald-600">{t.expected_dry_kg}</td>
                    <td className="text-right mono font-bold text-primary">{t.expected_pouches}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Row 2: Machine Allocation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-testid="factory-today-machines">
        {data.machine_allocation.map(m => {
          const trayPct = m.trays_total > 0 ? Math.round((m.trays_used / m.trays_total) * 100) : 0;
          const overloaded = m.trays_used > m.trays_total;
          return (
            <Card key={m.machine_id} className={`shadow-none ${overloaded ? "border-red-300" : ""}`}
                  data-testid={`mach-${m.machine_code}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-sky-100 text-sky-700 flex items-center justify-center">
                      <Snowflake size={20} weight="duotone"/>
                    </div>
                    <div>
                      <div className="font-display font-bold text-base tracking-tight">{m.machine_name}</div>
                      <div className="overline">{m.machine_code} · {m.status}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="overline">Trays</div>
                    <div className={`text-metric text-2xl ${overloaded ? "text-red-600" : ""}`}>
                      {m.trays_used}<span className="text-base text-muted-foreground">/{m.trays_total}</span>
                    </div>
                  </div>
                </div>
                <Progress value={Math.min(100, trayPct)} className={`h-2 mb-3 ${overloaded ? "bg-red-100" : ""}`}/>

                {m.batches.length > 0 ? (
                  <div className="space-y-1.5 mb-3">
                    {m.batches.map(b => (
                      <div key={b.batch_no} className="flex items-center justify-between text-xs rounded-md bg-muted/40 px-2.5 py-1.5">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold truncate">{b.sku_name}</div>
                          <div className="mono text-[10px] text-muted-foreground">{b.batch_no} · {b.stage}</div>
                        </div>
                        <div className="mono text-[11px] text-muted-foreground ml-2">
                          <span className="font-bold text-foreground">{b.trays}</span>t · {b.planned_kg}kg
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground italic mb-3">No batches loaded — idle capacity</div>
                )}

                <div className="flex items-center justify-between text-[11px] pt-2 border-t border-border">
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Clock size={11}/>Cycle {m.cycle_hours}h
                  </span>
                  <span className="mono font-bold">
                    {m.expected_finish
                      ? <>ETA <span className="text-primary">{fmtTime(m.expected_finish)}</span></>
                      : <span className="text-muted-foreground">no active load</span>}
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Row 3: Raw Materials + Employee Assignments */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="shadow-none lg:col-span-2" data-testid="factory-today-materials">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-base tracking-tight flex items-center gap-2">
                <ClipboardText size={16}/>Raw Material Requirement
              </div>
              <div className="text-[10px] text-muted-foreground">Aggregated from today's batches</div>
            </div>
            {data.raw_materials.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4">
                No consumption yet — create batches from the Planner or Batches page.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-72 overflow-auto">
                {data.raw_materials.map(m => (
                  <div key={m.material_id} className={`flex items-center justify-between rounded-md px-2.5 py-1.5 text-xs border ${
                    m.below_min ? "border-amber-300 bg-amber-50" : "border-border bg-card"
                  }`} data-testid={`mat-${m.material_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{m.name}</div>
                      <div className="text-[10px] text-muted-foreground">{m.category || m.unit}</div>
                    </div>
                    <div className="text-right ml-2 whitespace-nowrap">
                      <div className="mono font-bold">{m.required} <span className="text-muted-foreground text-[10px]">{m.unit}</span></div>
                      <div className={`mono text-[10px] ${m.below_min ? "text-amber-800 font-semibold" : "text-muted-foreground"}`}>
                        {m.below_min ? <><Warning size={9} className="inline"/> stock {m.in_stock}</> : <>in-stock {m.in_stock}</>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="lg:col-span-1">
          <div className="font-display font-bold text-base tracking-tight mb-2 flex items-center gap-2">
            <Factory size={16}/>Employee Assignment
          </div>
          <div className="grid grid-cols-1 gap-2">
            {DEPARTMENTS.map(d => (
              <AssignmentCard key={d.key} dept={d} names={data.assignments[d.key]} onSave={saveDept}/>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
